import React from "react";
import { Check, X, ArrowRight, Database, Zap, ShieldCheck, Users } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const crmLogos = [{
  name: "Salesforce",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_salesforce_logo.png"
}, {
  name: "HubSpot",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_hubspot_logo.png"
}, {
  name: "Zoho",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_zoho_logo.png"
}, {
  name: "Pipedrive",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_pipedrive_logo.png"
}, {
  name: "Zendesk",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_zendesk_logo.png"
}, {
  name: "Microsoft Dynamics",
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1572085/img/crm_dynamics_logo.png"
}];
const comparisonData = [{
  feature: "Caller Identification",
  standard: "Phone Number Only",
  mollycall: "Full Name, Company & Role"
}, {
  feature: "Context Awareness",
  standard: "None (Cold Start)",
  mollycall: "Recent Emails, Tickets & Deals"
}, {
  feature: "Intent Prediction",
  standard: "Keypad Menu (Press 1)",
  mollycall: "Predictive AI Analysis"
}, {
  feature: "Personalization",
  standard: "Generic Robotic Greeting",
  mollycall: "Hyper-Personalized & Human-like"
}, {
  feature: "Data Entry",
  standard: "Manual Post-Call Input",
  mollycall: "Real-time Auto-Sync"
}];
export default function CrmIntelligence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="crm-intelligence" ref={ref} className={`py-24 bg-background relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      {}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-blue-400/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {}
          <div className="space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Database className="w-4 h-4" />
                <span>ProfileSync Technology</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Your CRM, Now <span className="text-primary">Voice-Activated</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                MollyCall.ai doesn't just answer calls; it understands who is calling. 
                Our proprietary <strong>ProfileSync</strong> engine connects directly to your CRM 
                in milliseconds, retrieving customer history, deal status, and recent interactions 
                before the AI even says "Hello."
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20">
                  <Link to="/contact-us">
                    Integrate Your CRM <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-primary/20 hover:bg-primary/5">
                  <Link to="/technology">
                    View Documentation
                  </Link>
                </Button>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Trusted Integrations
              </h3>
              <div className="grid grid-cols-3 gap-6 md:gap-8">
                {crmLogos.map((logo, index) => <div key={index} className="group flex items-center justify-center p-4 rounded-xl bg-secondary/30 border border-transparent hover:border-primary/10 hover:bg-white hover:shadow-lg transition-all duration-300">
                    <img src={logo.src} alt={`${logo.name} logo`} className="h-8 w-auto object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300" />
                  </div>)}
              </div>
              <p className="text-sm text-muted-foreground">
                +50 more integrations available via API and Zapier.
              </p>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-2xl transform rotate-1 scale-105 blur-sm -z-10" />
            
            <Card className="border-border/50 shadow-xl bg-white/80 backdrop-blur-sm overflow-hidden">
              <CardHeader className="bg-secondary/20 border-b border-border/50 pb-6">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Zap className="w-5 h-5 text-primary" />
                  Intelligence Comparison
                </CardTitle>
                <CardDescription>
                  See the difference between standard IVR and MollyCall Intelligence.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-b border-border/50">
                      <TableHead className="w-[35%] pl-6 py-4 text-muted-foreground font-medium">Data Point</TableHead>
                      <TableHead className="w-[30%] py-4 text-muted-foreground font-medium">Standard IVR</TableHead>
                      <TableHead className="w-[35%] py-4 text-primary font-bold bg-primary/5">MollyCall AI</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparisonData.map((row, i) => <TableRow key={i} className="hover:bg-secondary/30 border-b border-border/50 last:border-0 transition-colors">
                        <TableCell className="pl-6 py-4 font-medium text-foreground">
                          {row.feature}
                        </TableCell>
                        <TableCell className="py-4 text-muted-foreground flex items-center gap-2">
                          <X className="w-4 h-4 text-red-400 shrink-0" />
                          <span className="text-sm">{row.standard}</span>
                        </TableCell>
                        <TableCell className="py-4 bg-primary/5 font-medium text-foreground relative">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-primary shrink-0" />
                            <span className="text-sm">{row.mollycall}</span>
                          </div>
                          {}
                          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
              <div className="p-6 bg-secondary/10 border-t border-border/50">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Enterprise-Grade Security</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      All data syncs are encrypted end-to-end. SOC2 Type II compliant infrastructure ensures your customer data remains secure.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}